import { Button, Flex, Layout, Tooltip, Typography } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { colorBgBrandless, colorPrimary } from 'src/app/themes/primaryThemeDefault'
import { useIssuedCheckProcessingLogContext } from './index.context'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomRangePicker from 'src/app/shared/components/common/range-picker'
import { isEmpty } from 'lodash'
import {
  ArrowRightIcon,
  CalendarCustomIcon,
  GrayRefreshIcon,
  InfoIcon,
  RefreshIcon,
  WhiteSearchIcon,
} from 'src/assets/icons'
import './index.style.scss'
import { useFormRules } from './index.constant'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { SYSTEM_ROLES } from 'src/app/shared/models/common.model'
import CustomInput from 'src/app/shared/components/common/input'
import CustomSelectAll from 'src/app/shared/components/common/select-all'
import { useDisableFutureDates } from 'src/app/shared/hooks/useDisableFutureDates'

const IssuedCheckProcessingLogContent: React.FC = () => {
  const { form, clients, loading, onSubmit, handleGetClients, loadingClients } = useIssuedCheckProcessingLogContext()
  const { control, handleSubmit, resetField, setError, clearErrors, trigger } = useMemo(() => form, [form])
  const { t } = useTranslation()
  const { errors, isValid, isDirty } = useMemo(() => form.formState, [form.formState])
  const rules = useFormRules()
  const userProfile = useSelector(getUserProfile)
  const userRole = userProfile?.role
  const retentionSettings = useSelector((state: any) => state?.masterSetup?.deferredConfig?.retentionSettings)
  const { issuedCheckFileProcessingLog, transactionAuditLog } = retentionSettings
  const { disableFutureDates } = useDisableFutureDates()

  const isButtonDisabled = !isValid || !isEmpty(errors) || !isDirty || loading || loadingClients

  const resetIssuedCheck = () => {
    resetField('clientId')
    resetField('date')
  }

  useEffect(() => {
    window.localStorage.removeItem('issuedCheckLog')
    handleGetClients()
  }, [])

  return (
    <Layout.Content
      style={{
        padding: 24,
      }}
      className='issued-check-processing-log'
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          style={{
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <Controller
            control={control}
            name='clientId'
            render={({ field, fieldState }) => {
              return userRole !== SYSTEM_ROLES.CLIENT_USER ? (
                <CustomSelectAll
                  label={t('IssuedCheckProcessingLog:issued_check_processing_log.label.client')}
                  placeholder={t('IssuedCheckProcessingLog:issued_check_processing_log.placeholder.client')}
                  value={field.value}
                  onChange={(data: any) => {
                    if (!data || data?.length === 0) {
                      form.setValue('clientId', [null])
                    } else if (data?.includes(null)) {
                      if (!data[0]) {
                        const newData = data?.slice(1)
                        form.setValue('clientId', [...newData])
                      } else {
                        form.setValue('clientId', [null])
                      }
                    } else {
                      form.setValue('clientId', data)
                    }
                  }}
                  maxTagCount={'responsive'}
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                      <span>+ {omittedValues.length}...</span>
                    </Tooltip>
                  )}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  options={
                    clients?.map((item) => ({
                      label: item.clientName,
                      value: item.id,
                    })) ?? []
                  }
                  haveAllOption
                  defaultActiveFirstOption
                  allowClear={!field.value?.includes(null)}
                  loading={loadingClients}
                  defaultOption={{
                    label: 'All Clients',
                    value: null,
                  }}
                  showSearch
                />
              ) : (
                <CustomInput
                  label={t('IssuedCheckProcessingLog:issued_check_processing_log.label.client')}
                  placeholder={t('IssuedCheckProcessingLog:issued_check_processing_log.placeholder.client')}
                  value={field.value ? field.value?.label : null}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  disabled
                />
              )
            }}
            rules={rules.clientId}
          />
          <Controller
            control={control}
            name='date'
            render={({ field, fieldState }) => (
              <CustomRangePicker
                label={t('IssuedCheckProcessingLog:issued_check_processing_log.label.date')}
                value={field.value}
                onChange={async (e: any) => {
                  field.onChange(e)
                  if (!e) {
                    setError('date', {
                      type: 'required',
                      message: t('IssuedCheckProcessingLog:validation.required.date'),
                    })
                  } else {
                    clearErrors('date')
                  }
                }}
                onBlur={async () => {
                  field.onBlur()
                  await trigger('date')
                }}
                error={fieldState.error}
                placeholder={[
                  t('IssuedCheckProcessingLog:issued_check_processing_log.placeholder.dateFrom'),
                  t('IssuedCheckProcessingLog:issued_check_processing_log.placeholder.dateTo'),
                ]}
                separator={<ArrowRightIcon className='issued-check-log-separator' />}
                suffixIcon={<CalendarCustomIcon />}
                required
                // allowEmpty={[true, true]}
                disabledDate={disableFutureDates} // only allow to choose dates from the past to current date
              />
            )}
            rules={rules.date}
          />
          <div
            style={{
              borderColor: colorPrimary,
              borderWidth: 1,
              borderStyle: 'solid',
              borderRadius: 12,
              backgroundColor: colorBgBrandless,
              padding: '12px 20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <InfoIcon width={28} height={28} />
            <Typography
              style={{
                fontSize: 16,
                fontFamily: 'MyriadPro',
              }}
            >
              {t('IssuedCheckProcessingLog:issued_check_processing_log.note', {
                issuedCheckProcessingLog: `${issuedCheckFileProcessingLog} ${
                  issuedCheckFileProcessingLog > 1 ? t('common:days') : t('common:day')
                }`,
                transactionAuditLog: `${transactionAuditLog} ${transactionAuditLog > 1 ? 'days' : 'day'}`,
              })}
            </Typography>
          </div>
          <Flex
            style={{
              justifyContent: 'flex-end',
              gap: 12,
            }}
            className='check-button-group'
          >
            <Button
              className='reset-btn'
              disabled={isButtonDisabled}
              onClick={resetIssuedCheck}
              icon={isButtonDisabled ? <GrayRefreshIcon /> : <RefreshIcon />}
            >
              {t('IssuedCheckProcessingLog:issued_check_processing_log.reset')}
            </Button>
            <Button
              type='primary'
              className='search-check-btn'
              disabled={isButtonDisabled}
              htmlType='button'
              onClick={handleSubmit(onSubmit)}
              icon={<WhiteSearchIcon />}
              loading={loading}
            >
              {t('IssuedCheckProcessingLog:issued_check_processing_log.search_btn')}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Layout.Content>
  )
}

export default IssuedCheckProcessingLogContent
